/**
 * O mapa do mundo: 7 regiões de 7 hexágonos, a "flor de flores".
 *
 * <p>A matemática de posicionamento vem do protótipo, que já a tinha validada: as
 * coordenadas axiais das regiões (`REGC`), o deslocamento das seis pétalas (`PET`) e a
 * conversão para pixel. Reescrevê-la seria refazer um trabalho que já estava certo.
 *
 * <p>O que <b>mudou</b> em relação ao protótipo: lá a posição de cada ficha era o
 * índice dela num array, então criar uma região sempre a jogava no primeiro slot livre
 * em vez do hexágono clicado. Aqui a posição é dado — o servidor recebe qual hexágono
 * foi clicado, e recusa slot ocupado.
 *
 * <p>Cada hexágono mostra <b>apenas</b> nome, ícones de perícia e a cor da região
 * (item 4 da spec). Tudo o mais é assunto da ficha da área.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PALETA_DO_MAPA } from './esquema';
import * as GEO from './geometriaDoMapa';
import { IconeDeRegiao } from './IconeDeRegiao';
import { TituloDaPagina } from '../../shell/TituloDaPagina';
import * as mundoApi from './mundoApi';
import { PericiaIcone } from './PericiaIcone';
import type { Mapa, Resumo } from './tipos';
import './mundo.css';

/*
 * A geometria mora em `geometriaDoMapa`, e não mais aqui: a tela de Áreas e Tier
 * desenha o mesmo mapa, e duas cópias das constantes divergiriam na primeira vez que
 * alguém movesse uma região.
 */
const { LADO, CENTROS, PETALAS, axialParaPixel, pontosDoHexagono, luminancia } = GEO;

interface Celula {
  chave: string;
  q: number;
  r: number;
  px: number;
  py: number;
  cor: string;
  /** Preenchimento: área existente, slot vago de região existente, ou região inexistente. */
  tipo: 'area' | 'vagoNaRegiao' | 'semRegiao';
  regiaoId?: number;
  regiaoNome?: string;
  slotDaRegiao: number;
  slotDaArea: number;
  areaId?: number;
  nome?: string;
  pericias: string[];
}

export function MundoMapa() {
  const navegar = useNavigate();
  const fila = useQueryClient();
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [arrastando, setArrastando] = useState<{ x: number; y: number } | null>(null);
  const [carregado, setCarregado] = useState<Celula | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const palco = useRef<HTMLDivElement>(null);
  const jaEnquadrou = useRef(false);

  const { data: mapa, isLoading } = useQuery<Mapa>({
    queryKey: ['mundo', 'mapa'],
    queryFn: mundoApi.carregarMapa,
  });

  /** O ícone de cada região: o endpoint do mapa desenha hexágonos, não guarda escolhas. */
  const { data: regioesDaLista } = useQuery<Resumo[]>({
    queryKey: ['mundo', 'regioes'],
    queryFn: () => mundoApi.listar('regioes'),
  });
  const iconePorRegiao = useMemo(() => {
    const porId = new Map<number, string | undefined>();
    (regioesDaLista ?? []).forEach((r) => porId.set(r.id, r.icone));
    return porId;
  }, [regioesDaLista]);

  const recarregar = () => {
    void fila.invalidateQueries({ queryKey: ['mundo'] });
  };

  const criarRegiao = useMutation({
    mutationFn: (v: { nome: string; indiceMapa: number }) => mundoApi.criar('regioes', v),
    onSuccess: recarregar,
    onError: (e) => setErro(e instanceof Error ? e.message : 'Falha ao criar.'),
  });

  const criarArea = useMutation({
    mutationFn: (v: { nome: string; indiceMapa: number; regiaoId: number }) =>
      mundoApi.criar('areas', v),
    onSuccess: recarregar,
    onError: (e) => setErro(e instanceof Error ? e.message : 'Falha ao criar.'),
  });

  const pintar = useMutation({
    mutationFn: (v: { id: number; cor: string }) =>
      mundoApi.salvar('regioes', v.id, { corMapa: v.cor }),
    onSuccess: recarregar,
  });

  const mover = useMutation({
    mutationFn: (v: { areaId: number; indiceMapa: number; regiaoId: number }) =>
      mundoApi.moverArea(v.areaId, v.indiceMapa, v.regiaoId),
    onSuccess: recarregar,
    onError: (e) => setErro(e instanceof Error ? e.message : 'Falha ao mover.'),
  });

  /** Mover a região muda a ordem dela em todas as listagens, não só no mapa. */
  const moverRegiaoNoMapa = useMutation({
    mutationFn: (v: { id: number; indiceMapa: number }) =>
      mundoApi.moverRegiao(v.id, v.indiceMapa),
    onSuccess: recarregar,
    onError: (e) => setErro(e instanceof Error ? e.message : 'Falha ao mover a região.'),
  });

  const { celulas, largura, altura, deslocX, deslocY } = useMemo(() => {
    const lista: Celula[] = [];
    (mapa?.regioes ?? []).forEach((regiao, i) => {
      const [cq, cr] = CENTROS[i];
      const cor = regiao.corMapa || PALETA_DO_MAPA[i % PALETA_DO_MAPA.length];
      const posicoes: [number, number][] = [
        [cq, cr],
        ...PETALAS.map(([dq, dr]) => [cq + dq, cr + dr] as [number, number]),
      ];

      posicoes.forEach((pos, slot) => {
        const hex = regiao.hexagonos.find((h) => h.indiceMapa === slot);
        const existe = regiao.id != null;
        lista.push({
          chave: `${i}-${slot}`,
          q: pos[0],
          r: pos[1],
          px: 0,
          py: 0,
          cor: existe ? cor : '#2A322D',
          tipo: hex?.areaId ? 'area' : existe ? 'vagoNaRegiao' : 'semRegiao',
          regiaoId: regiao.id,
          regiaoNome: regiao.nome,
          slotDaRegiao: i,
          slotDaArea: slot,
          areaId: hex?.areaId,
          nome: hex?.nome,
          pericias: hex?.pericias ?? [],
        });
      });
    });

    let minX = 1e9;
    let minY = 1e9;
    let maxX = -1e9;
    let maxY = -1e9;
    lista.forEach((c) => {
      const p = axialParaPixel(c.q, c.r);
      c.px = p.x;
      c.py = p.y;
      minX = Math.min(minX, p.x - LADO * 1.8);
      minY = Math.min(minY, p.y - LADO * 1.8);
      maxX = Math.max(maxX, p.x + LADO * 1.8);
      maxY = Math.max(maxY, p.y + LADO * 1.8);
    });

    return {
      celulas: lista,
      largura: maxX - minX,
      altura: maxY - minY,
      deslocX: -minX,
      deslocY: -minY,
    };
  }, [mapa]);

  /**
   * Enquadra o mundo inteiro no espaço disponível.
   *
   * <p>É o estado inicial e o destino do botão de centralizar. O mundo tem quase 1900
   * unidades de largura, então sem isto a tela abre num pedaço qualquer do mapa, com
   * zoom que não corresponde a nada — e o time precisa arrastar até achar as regiões.
   */
  const enquadrar = useCallback(() => {
    const caixa = palco.current?.getBoundingClientRect();
    if (!caixa || largura <= 0 || altura <= 0) return;

    // A folga de 6% evita que os hexágonos das pontas encostem na borda.
    const escala = Math.min(caixa.width / largura, caixa.height / altura) * 0.94;
    setZoom(escala);
    setPan({
      x: (caixa.width - largura * escala) / 2,
      y: (caixa.height - altura * escala) / 2,
    });
  }, [largura, altura]);

  // Enquadra assim que o mapa chega e o palco tem tamanho — uma vez só, para não
  // desfazer o zoom que a pessoa escolheu depois.
  useLayoutEffect(() => {
    if (jaEnquadrou.current || largura <= 0) return;
    if (!palco.current?.getBoundingClientRect().width) return;
    jaEnquadrou.current = true;
    enquadrar();
  }, [largura, enquadrar]);

  // Redimensionar a janela reenquadra: um mapa cortado ao meio não serve para nada.
  useEffect(() => {
    const alvo = palco.current;
    if (!alvo || typeof ResizeObserver === 'undefined') return;
    const observador = new ResizeObserver(() => {
      if (jaEnquadrou.current) enquadrar();
    });
    observador.observe(alvo);
    return () => observador.disconnect();
  }, [enquadrar]);

  /**
   * Muda o zoom mantendo fixo um ponto da tela.
   *
   * <p>Sem isto o mundo escapa para o canto: como a origem da transformação é o canto
   * superior esquerdo, aumentar a escala afasta tudo daquele ponto. Aqui o deslocamento
   * é corrigido para que o ponto de referência — o cursor, ou o centro da tela — continue
   * exatamente sobre o mesmo hexágono depois do zoom.
   */
  const aplicarZoom = useCallback(
    (novoZoom: number, ancoraX?: number, ancoraY?: number) => {
      const caixa = palco.current?.getBoundingClientRect();
      if (!caixa) return;

      const alvo = Math.min(2.5, Math.max(0.05, novoZoom));
      const x = ancoraX ?? caixa.width / 2;
      const y = ancoraY ?? caixa.height / 2;

      setPan((antes) => {
        // Onde, no mundo, está o ponto que a pessoa quer manter parado.
        const mundoX = (x - antes.x) / zoom;
        const mundoY = (y - antes.y) / zoom;
        return { x: x - mundoX * alvo, y: y - mundoY * alvo };
      });
      setZoom(alvo);
    },
    [zoom]
  );

  function aoClicar(celula: Celula) {
    setErro(null);

    if (celula.tipo === 'area') {
      navegar(`/mundo/areas/${celula.areaId}`);
      return;
    }

    if (celula.tipo === 'semRegiao') {
      // Só o hexágono central cria a região: as pétalas de uma região que não existe
      // não têm o que representar ainda.
      if (celula.slotDaArea !== 0) return;
      const nome = window.prompt(`Nome da região ${celula.slotDaRegiao + 1}:`);
      if (nome?.trim()) {
        criarRegiao.mutate({ nome: nome.trim(), indiceMapa: celula.slotDaRegiao });
      }
      return;
    }

    const nome = window.prompt(`Nome da nova área em "${celula.regiaoNome}":`);
    if (nome?.trim()) {
      criarArea.mutate({
        nome: nome.trim(),
        indiceMapa: celula.slotDaArea,
        regiaoId: celula.regiaoId!,
      });
    }
  }

  /**
   * Solta o que estava sendo arrastado.
   *
   * <p>Arrastar o hexágono <b>central</b> move a região inteira — e como a ordem das
   * regiões no mapa é a mesma das listagens, é assim que se decide qual vem primeiro.
   * Arrastar qualquer outro move só aquela área.
   */
  function soltarEm(destino: Celula) {
    if (!carregado || carregado.chave === destino.chave) return;

    if (carregado.slotDaArea === 0 && carregado.regiaoId != null && destino.slotDaArea === 0) {
      moverRegiaoNoMapa.mutate({
        id: carregado.regiaoId,
        indiceMapa: destino.slotDaRegiao,
      });
      setCarregado(null);
      return;
    }

    if (!destino.regiaoId || carregado.areaId == null) return;
    mover.mutate({
      areaId: carregado.areaId,
      indiceMapa: destino.slotDaArea,
      regiaoId: destino.regiaoId,
    });
    setCarregado(null);
  }

  if (isLoading) return <div className="mundo mu-nada">Carregando o mapa…</div>;

  return (
    <div className="mundo mapa-tela">
      <TituloDaPagina>
        7 regiões, 7 áreas cada. Clique num hexágono vazio para criar. Arraste uma área
        para movê-la; arraste o hexágono <b>central</b> de uma região sobre o de outra
        para trocar a ordem delas — que é a mesma ordem das listagens.
      </TituloDaPagina>

      {erro && <div className="mapa-erro">{erro}</div>}

      <div className="mapa-quadro">
        <div
          className={`mapa-palco${arrastando ? ' movendo' : ''}`}
          ref={palco}
          onMouseDown={(e) => {
            if ((e.target as HTMLElement).closest('.hx-rot')) return;
            setArrastando({ x: e.clientX - pan.x, y: e.clientY - pan.y });
          }}
          onMouseMove={(e) => {
            if (arrastando) setPan({ x: e.clientX - arrastando.x, y: e.clientY - arrastando.y });
          }}
          onMouseUp={() => setArrastando(null)}
          onMouseLeave={() => setArrastando(null)}
          onWheel={(e) => {
            const caixa = palco.current?.getBoundingClientRect();
            if (!caixa) return;
            // Passo proporcional: zoom fica igualmente sensível perto e longe.
            const fator = e.deltaY < 0 ? 1.12 : 1 / 1.12;
            aplicarZoom(zoom * fator, e.clientX - caixa.left, e.clientY - caixa.top);
          }}
        >
          <div
            className="mapa-mundo"
            style={{
              width: largura,
              height: altura,
              // Origem no canto: assim o par (pan, zoom) descreve exatamente onde o
              // mundo está, e "enquadrar" vira uma conta simples em vez de tentativa.
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            }}
          >
            <svg width={largura} height={altura} viewBox={`0 0 ${largura} ${altura}`}>
              {celulas.map((c) => (
                <polygon
                  key={c.chave}
                  points={pontosDoHexagono(c.px + deslocX, c.py + deslocY)}
                  fill={c.cor}
                  fillOpacity={c.tipo === 'area' ? 1 : c.tipo === 'vagoNaRegiao' ? 0.3 : 0.16}
                  stroke="#0B0F0D"
                  strokeWidth={7}
                />
              ))}
            </svg>

            {celulas.map((c) => {
              const claro = luminancia(c.cor) > 0.55;
              const icone = iconePorRegiao.get(c.regiaoId ?? -1);
              return (
                <div
                  key={c.chave}
                  className={`hx-rot ${c.tipo}${carregado?.chave === c.chave ? ' pego' : ''}`}
                  style={{
                    left: c.px + deslocX,
                    top: c.py + deslocY,
                    color: claro ? '#14110C' : '#F3ECDD',
                  }}
                  // Central de região existente arrasta a região; área arrasta a área.
                  draggable={c.tipo === 'area' || (c.slotDaArea === 0 && c.regiaoId != null)}
                  onDragStart={() => setCarregado(c)}
                  onDragOver={(e) => c.regiaoId != null && e.preventDefault()}
                  onDrop={() => soltarEm(c)}
                  onClick={() => aoClicar(c)}
                  title={
                    c.slotDaArea === 0 && c.regiaoId != null
                      ? `${c.regiaoNome} — arraste sobre outra região para trocar a ordem`
                      : c.tipo === 'area'
                        ? `${c.nome} — abrir a ficha`
                        : c.tipo === 'vagoNaRegiao'
                          ? `Criar área em ${c.regiaoNome}`
                          : c.slotDaArea === 0
                            ? `Criar a região ${c.slotDaRegiao + 1}`
                            : ''
                  }
                >
                  {c.tipo === 'area' && (
                    <>
                      <div className="hx-nome">{c.nome || '—'}</div>
                      {c.pericias.length > 0 && (
                        <div className="hx-per">
                          {c.pericias.map((p) => {
                            const info = mapa?.pericias.find((x) => x.id === p);
                            return (
                              <PericiaIcone
                                key={p}
                                pericia={p}
                                cor={info?.cor}
                                titulo={info?.rotulo}
                                tamanho={30}
                              />
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                  {c.tipo === 'vagoNaRegiao' && (
                    <>
                      {/* O hexágono central de uma região sem áreas mostra o ícone
                          dela: é o que identifica a região no mapa antes de ela ter
                          qualquer conteúdo. */}
                      {c.slotDaArea === 0 && icone && (
                        <IconeDeRegiao
                          icone={icone}
                          cor={claro ? '#14110C' : '#F3ECDD'}
                          tamanho={40}
                          titulo={c.regiaoNome}
                        />
                      )}
                      <div className="hx-mais">+</div>
                    </>
                  )}
                  {c.tipo === 'semRegiao' && c.slotDaArea === 0 && (
                    <div className="hx-vazia">Região {c.slotDaRegiao + 1}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="mapa-legenda">
          <div className="mapa-leg-tit">Regiões</div>
          {(mapa?.regioes ?? []).map((r, i) =>
            r.id == null ? null : (
              <label key={r.id} className="mapa-leg-item">
                <input
                  type="color"
                  value={r.corMapa || PALETA_DO_MAPA[i % PALETA_DO_MAPA.length]}
                  onChange={(e) => pintar.mutate({ id: r.id!, cor: e.target.value })}
                  title="Cor no mapa"
                />
                <IconeDeRegiao
                  icone={iconePorRegiao.get(r.id)}
                  cor={r.corMapa || PALETA_DO_MAPA[i % PALETA_DO_MAPA.length]}
                  tamanho={16}
                />
                <span>{r.nome}</span>
              </label>
            )
          )}
          {(mapa?.regioes ?? []).every((r) => r.id == null) && (
            <div className="mapa-leg-vazio">Nenhuma região ainda</div>
          )}

          <div className="mapa-leg-tit">Perícias</div>
          {(mapa?.pericias ?? []).map((p) => (
            <div key={p.id} className="mapa-leg-item">
              <PericiaIcone pericia={p.id} cor={p.cor} tamanho={16} />
              <span>{p.rotulo}</span>
            </div>
          ))}
        </aside>

        {/* No canto inferior esquerdo, como no protótipo — longe da legenda e fora do
            caminho de quem arrasta o mapa. */}
        <div className="mapa-zoom">
          <button onClick={() => aplicarZoom(zoom / 1.2)} title="Afastar">
            −
          </button>
          <button onClick={() => aplicarZoom(zoom * 1.2)} title="Aproximar">
            +
          </button>
          <button className="ajustar" onClick={enquadrar}>
            Ajustar
          </button>
          <span className="mapa-pct">{Math.round(zoom * 100)}%</span>
        </div>
      </div>
    </div>
  );
}
